import { Checkbox } from '@innovationdepartment/proxima-ui';
import {
  type CellContext,
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable
} from '@tanstack/react-table';
import { useShowSpinner } from 'hooks';
import useBrandTagsApi from 'hooks/useBrandTagsApi';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { FetchAllBrandTag } from 'types/brandTag';
import { formatDateTimeFromIso } from 'utils/formatDate';
import type { BrandTagListProps, ColumnConfig, FilterConfig } from './BrandTagList.types';
import BrandTagListView from './BrandTagList.View';
import { ALL_OPTIONS } from './BrandTags.constants';

const defaultColumnConfig: ColumnConfig = {
  id: true,
  displayName: true,
  category: true,
  subCategory: true,
  genderRelevance: true,
  personaEligible: true,
  sensitive: true,
  deprecated: true,
  brandsUsingTag: true,
  updatedAt: true
};

const defaultFilterConfig: FilterConfig = {
  search: true,
  category: true,
  subCategory: true,
  gender: true,
  personaEligible: true,
  sensitive: true,
  hideDeprecated: true
};

// Define default empty Set outside component to prevent recreation on every render
const defaultSelectedTagIds = new Set<string>();

const BrandTagListContainer = ({
  columnConfig = defaultColumnConfig,
  filterConfig = defaultFilterConfig,
  tableHeight,
  onTagClick,
  showHeader = true,
  activeOnly = false,
  selectedTagIds = defaultSelectedTagIds,
  onTagSelectionChange,
  onSelectAllClick
}: BrandTagListProps) => {
  const navigate = useNavigate();
  const { loading, fetchTags, fetchActiveTags } = useBrandTagsApi();
  const [tags, setTags] = useState<FetchAllBrandTag[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(ALL_OPTIONS);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>(ALL_OPTIONS);
  const [selectedGender, setSelectedGender] = useState<string>(ALL_OPTIONS);
  const [filters, setFilters] = useState({
    personaEligible: false,
    sensitive: false,
    hideDeprecated: false
  });
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'updatedAt',
      desc: true
    }
  ]);
  const [resetKey, setResetKey] = useState(0);
  const [subCategoryKey, setSubCategoryKey] = useState(0);
  const [distinctOptions, setDistinctOptions] = useState({
    categories: [] as string[],
    subCategories: [] as string[],
    genders: [] as string[]
  });
  const [categorySubCategoryMap, setCategorySubCategoryMap] = useState<Record<string, string[]>>(
    {}
  );
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [filteredTags, setFilteredTags] = useState<FetchAllBrandTag[]>([]);
  const [allColumnDefinitions, setAllColumnDefinitions] = useState<
    Record<keyof FetchAllBrandTag | 'select', ColumnDef<FetchAllBrandTag>>
  >({} as Record<keyof FetchAllBrandTag | 'select', ColumnDef<FetchAllBrandTag>>);
  const [columns, setColumns] = useState<ColumnDef<FetchAllBrandTag>[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  useShowSpinner({ show: loading });

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (activeOnly) {
      fetchActiveTags().then(setTags);
    } else {
      fetchTags().then(setTags);
    }
  }, [activeOnly]);

  useEffect(() => {
    const categories = Array.from(new Set(tags.map(tag => tag.category))).sort();
    const allSubCategories = Array.from(new Set(tags.map(tag => tag.subCategory))).sort();
    const genders = Array.from(new Set(tags.map(tag => tag.genderRelevance))).sort();

    // Build map of category -> subcategories[]
    const catSubCatMap: Record<string, string[]> = {};
    tags.forEach(tag => {
      if (!catSubCatMap[tag.category]) {
        catSubCatMap[tag.category] = [];
      }
      if (!catSubCatMap[tag.category].includes(tag.subCategory)) {
        catSubCatMap[tag.category].push(tag.subCategory);
      }
    });

    Object.keys(catSubCatMap).forEach(cat => {
      catSubCatMap[cat].sort();
    });

    setDistinctOptions({
      categories,
      subCategories: allSubCategories,
      genders
    });
    setCategorySubCategoryMap(catSubCatMap);
  }, [tags]);

  const debouncedSearch = debounce((value: string) => {
    setSearchTerm(value);
  }, 300);

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const handleCreateTag = () => {
    navigate('/brand-tags/create');
  };

  const handleFilterChange = (filter: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      [filter]: checked
    }));
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setSelectedSubCategory(ALL_OPTIONS);
    setSubCategoryKey(prev => prev + 1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(ALL_OPTIONS);
    setSelectedSubCategory(ALL_OPTIONS);
    setSelectedGender(ALL_OPTIONS);
    setFilters({
      personaEligible: false,
      sensitive: false,
      hideDeprecated: false
    });
    setResetKey(prev => prev + 1);
  };

  const matchesSearch = (tag: FetchAllBrandTag, search: string): boolean => {
    if (!search) return true;

    const lowerSearch = search.toLowerCase();
    const searchableFields = [
      tag.id,
      tag.displayName,
      tag.category,
      tag.subCategory,
      tag.description,
      tag.genderRelevance
    ];

    return searchableFields.some(field => field.toLowerCase().includes(lowerSearch));
  };

  const availableSubCategories =
    selectedCategory && selectedCategory !== ALL_OPTIONS
      ? categorySubCategoryMap[selectedCategory] || []
      : distinctOptions.subCategories;

  useEffect(() => {
    setHasActiveFilters(
      searchTerm !== '' ||
        selectedCategory !== ALL_OPTIONS ||
        selectedSubCategory !== ALL_OPTIONS ||
        selectedGender !== ALL_OPTIONS ||
        filters.personaEligible ||
        filters.sensitive ||
        filters.hideDeprecated
    );
  }, [searchTerm, selectedCategory, selectedSubCategory, selectedGender, filters]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    let result = tags.filter(tag => matchesSearch(tag, searchTerm));

    // Apply dropdown filters
    if (selectedCategory && selectedCategory !== ALL_OPTIONS) {
      result = result.filter(tag => tag.category === selectedCategory);
    }

    if (typeof selectedSubCategory === 'string' && selectedSubCategory !== ALL_OPTIONS) {
      result = result.filter(tag => tag.subCategory === selectedSubCategory);
    }

    if (selectedGender && selectedGender !== ALL_OPTIONS) {
      result = result.filter(tag => tag.genderRelevance === selectedGender);
    }

    // Apply checkbox filters
    if (filters.personaEligible) {
      result = result.filter(tag => tag.personaEligible);
    }

    if (filters.sensitive) {
      result = result.filter(tag => tag.sensitive);
    }

    if (filters.hideDeprecated) {
      result = result.filter(tag => !tag.deprecated);
    }

    setFilteredTags(result);
  }, [tags, searchTerm, selectedCategory, selectedSubCategory, selectedGender, filters]);

  // Selection handlers
  const handleToggleTagSelection = useCallback(
    (tagId: string) => {
      if (!onTagSelectionChange) return;
      const newSet = new Set(selectedTagIds);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      onTagSelectionChange(newSet);
    },
    [selectedTagIds, onTagSelectionChange]
  );

  const handleToggleSelectAll = useCallback(() => {
    if (onSelectAllClick) onSelectAllClick(filteredTags.map(tag => tag.id));
  }, [onSelectAllClick, filteredTags]);

  useEffect(() => {
    setIsAllSelected(selectedTagIds.size === filteredTags.length && filteredTags.length > 0);
  }, [selectedTagIds, filteredTags]);

  const renderSelectHeader = useCallback(
    () => <Checkbox checked={isAllSelected} onChange={handleToggleSelectAll} />,
    [isAllSelected, handleToggleSelectAll]
  );

  const renderSelectCell = useCallback(
    (info: CellContext<FetchAllBrandTag, unknown>) => (
      <Checkbox
        checked={selectedTagIds.has(info.row.original.id)}
        onChange={() => handleToggleTagSelection(info.row.original.id)}
      />
    ),
    [selectedTagIds, handleToggleTagSelection]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    setAllColumnDefinitions({
      select: {
        id: 'select',
        header: renderSelectHeader,
        cell: renderSelectCell,
        enableSorting: false,
        size: 50
      },
      id: {
        accessorKey: 'id',
        header: 'ID',
        cell: info => info.getValue()
      },
      displayName: {
        accessorKey: 'displayName',
        header: 'NAME',
        cell: info => info.getValue()
      },
      category: {
        accessorKey: 'category',
        header: 'CATEGORY',
        cell: info => info.getValue()
      },
      subCategory: {
        accessorKey: 'subCategory',
        header: 'SUB-CAT',
        cell: info => info.getValue()
      },
      genderRelevance: {
        accessorKey: 'genderRelevance',
        header: 'GENDER',
        cell: info => info.getValue()
      },
      description: {
        accessorKey: 'description',
        header: 'DESCRIPTION',
        cell: info => info.getValue()
      },
      personaEligible: {
        accessorKey: 'personaEligible',
        header: 'ELIGIBLE',
        cell: info => (info.getValue() ? '✓' : '')
      },
      sensitive: {
        accessorKey: 'sensitive',
        header: 'SENSITIVE',
        cell: info => (info.getValue() ? '✓' : '')
      },
      deprecated: {
        accessorKey: 'deprecated',
        header: 'DEPRECATED',
        cell: info => (info.getValue() ? '✓' : '')
      },
      brandsUsingTag: {
        accessorKey: 'brandsUsingTag',
        header: '# BRANDS',
        cell: info => info.getValue(),
        enableSorting: true
      },
      createdAt: {
        accessorKey: 'createdAt',
        header: 'CREATED',
        cell: info => formatDateTimeFromIso(info.getValue() as string),
        enableSorting: true
      },
      updatedAt: {
        accessorKey: 'updatedAt',
        header: 'MODIFIED',
        cell: info => formatDateTimeFromIso(info.getValue() as string),
        enableSorting: true
      },
      createdBy: {
        accessorKey: 'createdBy',
        header: 'CREATED BY',
        cell: info => (info.getValue() as string).replace('google-apps|', '')
      }
    });
  }, []);

  useEffect(() => {
    // Only set columns if allColumnDefinitions is ready
    if (!allColumnDefinitions.id) {
      return;
    }

    const columnKeys: (keyof FetchAllBrandTag)[] = [
      'id',
      'displayName',
      'category',
      'subCategory',
      'genderRelevance',
      'description',
      'personaEligible',
      'sensitive',
      'deprecated',
      'brandsUsingTag',
      'createdAt',
      'createdBy'
    ];

    const cols = columnKeys.filter(key => columnConfig[key]).map(key => allColumnDefinitions[key]);

    if (columnConfig.updatedAt) {
      cols.push(allColumnDefinitions.updatedAt);
    }

    setColumns(cols);
  }, [columnConfig, allColumnDefinitions]);

  const table = useReactTable({
    data: filteredTags,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  // Handle row click - use custom callback if provided, otherwise navigate to detail
  const handleRowClick = (tag: FetchAllBrandTag) => {
    if (onTagClick) {
      onTagClick(tag);
    } else {
      navigate(`/brand-tags/${tag.id}`);
    }
  };

  return (
    <BrandTagListView
      table={table}
      onSearch={handleSearch}
      onCreateTag={handleCreateTag}
      onFilterChange={handleFilterChange}
      filters={filters}
      categoryOptions={distinctOptions.categories}
      subCategoryOptions={availableSubCategories}
      genderOptions={distinctOptions.genders}
      selectedCategory={selectedCategory}
      selectedSubCategory={selectedSubCategory}
      selectedGender={selectedGender}
      onCategoryChange={handleCategoryChange}
      onSubCategoryChange={setSelectedSubCategory}
      onGenderChange={setSelectedGender}
      onRowClick={handleRowClick}
      onClearFilters={handleClearFilters}
      hasActiveFilters={hasActiveFilters}
      resetKey={resetKey}
      subCategoryKey={subCategoryKey}
      filterConfig={filterConfig}
      tableHeight={tableHeight}
      showHeader={showHeader}
    />
  );
};

export default BrandTagListContainer;
